import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCategories } from "../../hooks/useCategories";
import { useListings } from "../../hooks/useListings";
import { Loader2 } from "lucide-react";
import useLocation from "../../hooks/useLocation";
import PostForm from "../../components/PostForm";
import { useToast } from "../../admin/components/Toast";

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const uploadToCloudinary = async (file) => {
  const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  const res = await fetch(url, { method: "POST", body: formData });
  if (!res.ok) throw new Error("Cloudinary upload failed");
  const data = await res.json();
  return data.secure_url;
};

const staticFields = [
  {
    label: "post_title",
    type: "text",
    required: true,
    placeholder: "Title of your post",
  },
  {
    label: "description",
    type: "text",
    required: true,
    placeholder: "Describe your item, service, or need",
  },
  {
    label: "contact_email",
    type: "text",
    required: false,
    placeholder: "Your email address",
  },
  { label: "relay_email", type: "checkbox", required: false, placeholder: "" },
  {
    label: "contact_name",
    type: "text",
    required: false,
    placeholder: "Your name (optional)",
  },
  {
    label: "phone_number",
    type: "text",
    required: false,
    placeholder: "Your phone number (optional)",
  },
  {
    label: "phone_extension",
    type: "text",
    required: false,
    placeholder: "Phone extension (optional)",
  },
  {
    label: "show_phone_number",
    type: "checkbox",
    required: false,
    placeholder: "",
  },
  {
    label: "phone_calls_ok",
    type: "checkbox",
    required: false,
    placeholder: "",
  },
  { label: "text_sms_ok", type: "checkbox", required: false, placeholder: "" },
  {
    label: "show_post_to_disabled_people",
    type: "checkbox",
    required: false,
    placeholder: "",
  },
  {
    label: "images",
    type: "file",
    required: false,
    placeholder: "",
    multiple: true,
  },
];

const CreatePost = () => {
  const { category_id } = useParams();
  const navigate = useNavigate();
  const { categories, getPostFormTemplate } = useCategories();
  const { addListing, addError, guestAddListing } = useListings();
  const [postFormFields, setPostFormFields] = useState([]);
  const [postFormLoading, setPostFormLoading] = useState(false);
  const [postFormError, setPostFormError] = useState(null);
  const [postFormValues, setPostFormValues] = useState({});
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [posting, setPosting] = useState(false);
  const { getLocation } = useLocation();
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestMode, setGuestMode] = useState(false);
  const [guestSuccess, setGuestSuccess] = useState(false);
  const [guestError, setGuestError] = useState("");
  const [guestReady, setGuestReady] = useState(false);
  const { addToast } = useToast();

  const currentCategory = categories.find(
    (cat) => String(cat.id) === String(category_id)
  );

  useEffect(() => {
    const fetchForm = async () => {
      setPostFormLoading(true);
      setPostFormError(null);
      try {
        const detail = await getPostFormTemplate(category_id);
        const fields =
          detail?.form_fields ||
          detail?.fields ||
          detail?.data?.form_fields ||
          [];
        setPostFormFields([...(Array.isArray(fields) ? fields : [])]);
      } catch (err) {
        setPostFormError("Failed to load post form fields.");
        setPostFormFields([]);
      } finally {
        setPostFormLoading(false);
      }
    };
    fetchForm();
  }, [category_id, getPostFormTemplate]);

  const isLoggedIn = !!localStorage.getItem("token");

  // Guest post submission
  const handleGuestSubmit = async (
    formValues,
    staticFields,
    categoryId,
    postFormFields
  ) => {
    setPosting(true);
    setGuestError("");
    // Build payload to match GuestListingCreate
    const payload = {};
    // Static fields
    staticFields.forEach((field) => {
      if (field.label === "images") {
        payload.images = Array.isArray(formValues.images)
          ? formValues.images.filter((url) => typeof url === "string")
          : [];
      } else if (field.type === "checkbox") {
        payload[field.label] = !!formValues[field.label];
      } else {
        if (
          formValues[field.label] !== undefined &&
          formValues[field.label] !== ""
        ) {
          payload[field.label] = formValues[field.label];
        }
      }
    });
    // Collect all attr_* fields into attributes
    const attributes = {};
    Object.keys(formValues).forEach((key) => {
      if (key.startsWith("attr_")) {
        const attrKey = key.replace(/^attr_/, "");
        attributes[attrKey] = formValues[key];
      }
    });
    payload.attributes = attributes;
    // Add category_id
    payload.category_id = Number(category_id);
    // Add location
    try {
      const location = await getLocation();
      payload.country = location.country;
      payload.city = location.city;
      payload.region = location.region;
      payload.longitude = location.longitude;
      payload.latitude = location.latitude;
      // Force guest_email to be present right before sending
      payload.guest_email = guestEmail;
      // Ensure category_id is a number
      payload.category_id = Number(category_id);
      // console.log("[CREATEPOST DEBUG] Final guest payload:",payload,"category_id type:",typeof payload.category_id);
      const newListing = await guestAddListing(payload);
      if (newListing && newListing.management_token) {
        navigate(`/guest-payment/${newListing.management_token}`);
      } else {
        setGuestSuccess(true);
      }
    } catch (err) {
      setGuestError(err.message || "Failed to submit as guest");
    } finally {
      setPosting(false);
    }
  };

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Category not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 font-sans text-lg relative">
      {posting && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white px-6 py-4 rounded shadow text-lg font-semibold flex items-center gap-2">
            <Loader2 className="animate-spin h-5 w-5 text-gray-700" />
            Posting...
          </div>
        </div>
      )}
      {/* Guest modal logic */}
      {!isLoggedIn && !guestMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">Continue as:</h2>
            <div className="flex flex-col gap-4">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={() => navigate("/signup")}
              >
                Create Account
              </button>
              <button
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                onClick={() => setGuestMode(true)}
              >
                Post as Guest
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Guest email modal */}
      {!isLoggedIn && guestMode && !guestSuccess && !guestReady && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">Post as Guest</h2>
            <label className="block text-sm mb-2">
              Enter your email to receive a management link:
            </label>
            <input
              type="email"
              className="border border-gray-300 px-2 py-1 w-full rounded mb-4"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              required
            />
            {guestError && (
              <div className="text-red-600 text-xs mb-2">{guestError}</div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                onClick={() => setGuestMode(false)}
                disabled={posting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                onClick={() => setGuestReady(true)}
                disabled={!guestEmail || posting}
                type="button"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Guest post success */}
      {!isLoggedIn && guestSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center">
            <h2 className="text-lg font-bold mb-4">Success!</h2>
            <p className="mb-4">
              A management link has been sent to your email. Use it to edit or
              manage your post.
            </p>
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => navigate("/")}
            >
              Go to Home
            </button>
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto pt-8 px-4 pb-12">
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:underline text-xs mb-4"
        >
          &lt; back
        </button>
        <h2 className="text-base font-normal mb-6">
          create a posting in {currentCategory?.name}
        </h2>
        {postFormLoading ? (
          <div className="text-xs text-gray-600 mb-4">loading form...</div>
        ) : postFormError ? (
          <div className="text-xs text-red-600 mb-4">{postFormError}</div>
        ) : (
          (isLoggedIn || (guestMode && guestReady)) && (
            <PostForm
              mode="create"
              initialValues={{}}
              categoryId={category_id}
              guestEmail={!isLoggedIn ? guestEmail : undefined}
              onSubmit={
                isLoggedIn
                  ? async (
                      formValues,
                      staticFields,
                      categoryId,
                      postFormFields
                    ) => {
                      setPosting(true);
                      const newValues = { ...formValues };
                      staticFields.forEach((field) => {
                        if (
                          field.type === "checkbox" &&
                          !(field.label in newValues)
                        ) {
                          newValues[field.label] = false;
                        }
                      });
                      postFormFields.forEach((field) => {
                        if (field.type === "checkbox") {
                          const key = `attr_${field.label}`;
                          if (!(key in newValues)) {
                            newValues[key] = false;
                          }
                        }
                      });
                      if (Array.isArray(newValues.images)) {
                        newValues.images = newValues.images.filter(
                          (url) => typeof url === "string"
                        );
                      } else {
                        newValues.images = [];
                      }
                      try {
                        const token = localStorage.getItem("token");
                        // Get user location
                        const location = await getLocation();
                        newValues.country = location.country;
                        newValues.city = location.city;
                        newValues.region = location.region;
                        newValues.latitude = location.latitude;
                        newValues.longitude = location.longitude;
                      
                        const newListing = await addListing(
                          newValues,
                          staticFields,
                          category_id,
                          postFormFields,
                          token
                        );
                        // Only redirect to payment page if category.price is not 0
                        if (
                          newListing &&
                          newListing.id &&
                          currentCategory &&
                          Number(currentCategory.price) > 0
                        ) {
                          navigate(`/payment/${newListing.id}`);
                        } else {
                          navigate("/my-posts");
                        }
                      } catch (err) {
                        addToast(
                          err?.response?.data?.detail ||
                            "Failed to submit post. Please try again. ",
                          "error"
                        );
                      } finally {
                        setPosting(false);
                      }
                    }
                  : handleGuestSubmit
              }
              submitLabel="Create Post"
              loading={posting}
            />
          )
        )}
      </div>
    </div>
  );
};

export default CreatePost;
